import React from 'react'
import { useSelector } from 'react-redux'
import { createSelector } from 'reselect'

import {
  BOOKMARKED_NOTES,
  ARCHIVED_NOTES,
  TEMPLATE_NOTES,
} from 'data/filters/types'
import { selectors as UISelectors } from 'data/ui'
import { selectors as NotesSelectors } from 'data/notes'
import { selectors as ChannelsSelectors } from 'data/channels'
import { getChannelFavoriteIds } from 'data/userPreferences/selectors'

import useEntries from './useEntries'

import { FAVORITE_CHANNELS_SECTION_ID } from '../Items/SectionHeader/FavoriteChannelSectionHeader'
import { ALL_CHANNELS_SECTION_ID } from '../Items/SectionHeader/AllChannelsSectionHeader'
import { BOOKMARKED_NOTES_SECTION_ID } from '../Items/SectionHeader/BookmarkedNotesSectionHeader'
import { TEMPLATE_NOTES_SECTION_ID } from '../Items/Filters/Templates'
import { ARCHIVED_SECTION_ID } from '../Items/Filters/Archived'

const INITIAL_EXPAND_STATE = new Set([FAVORITE_CHANNELS_SECTION_ID])

// returns the IDs of rows that behave like channels
const channelLikeIdsSelector = createSelector(
  ChannelsSelectors.getChannels,
  channels =>
    channels
      .map(channel => channel.id)
      .toArray()
      .concat([
        BOOKMARKED_NOTES_SECTION_ID,
        TEMPLATE_NOTES_SECTION_ID,
        ARCHIVED_SECTION_ID,
      ])
)

const getPersonalChannelId = createSelector(
  ChannelsSelectors.getPersonalChannel,
  personalChannel => personalChannel && personalChannel.id
)

export default function useNavigation() {
  const [lastClickedNoteId, setLastClickedNoteId] = React.useState(null)
  const activeFilterId = useSelector(UISelectors.getActiveFilterId)
  const personalChannelId = useSelector(getPersonalChannelId)
  const activeTemplateId = useSelector(UISelectors.getActiveTemplateId)
  const activeChannelId = useSelector(UISelectors.getActiveChannelId)
  const activeNoteId = useSelector(UISelectors.getActiveNoteId)
  const channelLikeIds = useSelector(channelLikeIdsSelector)
  const favoriteChannelIds = useSelector(getChannelFavoriteIds)
  const activeEntryId =
    activeNoteId || activeTemplateId || activeChannelId || activeFilterId

  const [expandedEntryIds, setExpandedEntryIds] = React.useState(
    () => INITIAL_EXPAND_STATE
  )

  const toggleExpandEntry = React.useCallback(
    (
      id,
      {
        collapseChannels = false,
        checkRule42 = false,
        closeAllChannels = false,
      } = {}
    ) => {
      const expandingAChannelLikeOrSection = [
        // expanding these should close all channels
        FAVORITE_CHANNELS_SECTION_ID,
        ARCHIVED_SECTION_ID,
        TEMPLATE_NOTES_SECTION_ID,
        BOOKMARKED_NOTES_SECTION_ID,
      ].some(_id => _id === id && !expandedEntryIds.has(_id))

      if (expandingAChannelLikeOrSection) {
        expandedEntryIds.delete(ALL_CHANNELS_SECTION_ID)
      }

      if (expandedEntryIds.has(id)) {
        expandedEntryIds.delete(id)
      } else {
        if (collapseChannels) {
          channelLikeIds.forEach(channelId => {
            expandedEntryIds.delete(channelId)
          })

          if (checkRule42) {
            if (favoriteChannelIds.includes(id) || id === personalChannelId) {
              expandedEntryIds.add(FAVORITE_CHANNELS_SECTION_ID)
              expandedEntryIds.delete(ALL_CHANNELS_SECTION_ID)
            } else {
              expandedEntryIds.add(ALL_CHANNELS_SECTION_ID)
            }
          }

          if (closeAllChannels) {
            expandedEntryIds.delete(ALL_CHANNELS_SECTION_ID)
          }
        }
        expandedEntryIds.add(id)
      }
      setExpandedEntryIds(new Set(expandedEntryIds))
    },
    [expandedEntryIds, channelLikeIds, favoriteChannelIds, personalChannelId]
  )

  useExpandActivePath({
    setExpandedEntryIds,
    expandedEntryIds,
    channelLikeIds,
    lastClickedNoteId,
    personalChannelId,
  })

  const [scrollableEntries, bottomEntries] = useEntries({
    activeEntryId,
    expandedEntryIds,
    onToggleExpand: toggleExpandEntry,
    setLastClickedNoteId,
  })

  return {
    activeEntryId,

    scrollableEntries,
    bottomEntries,
    expandedEntryIds,
  }
}

function useExpandActivePath({
  setExpandedEntryIds,
  expandedEntryIds,
  channelLikeIds,
  lastClickedNoteId,
  personalChannelId,
}) {
  const activeTemplateId = useSelector(UISelectors.getActiveTemplateId)
  const activeChannelId = useSelector(UISelectors.getActiveChannelId)
  const activeNoteId = useSelector(UISelectors.getActiveNoteId)
  const activeNote = useSelector(NotesSelectors.getActiveNote)
  const activeNoteAncestorsIds = useSelector(
    NotesSelectors.getActiveNoteAncestorsIds
  )

  // let activeNotePathIds = []
  const activeFilterId = useSelector(UISelectors.getActiveFilterId)
  const favoriteChannelIds = useSelector(getChannelFavoriteIds)
  const personalChannel = useSelector(ChannelsSelectors.getPersonalChannel)

  const favoriteSectionIds = personalChannel
    ? favoriteChannelIds.concat(personalChannel.id)
    : favoriteChannelIds

  const newExpandedEntryIds = new Set()

  if (!activeTemplateId && activeNote) {
    if (activeFilterId !== BOOKMARKED_NOTES.id) {
      channelLikeIds.forEach(channelId => {
        newExpandedEntryIds.delete(channelId)
      })

      // expand the note's channel, except if it is archived, in which case open the archives and close "all notes"
      if (activeFilterId === ARCHIVED_NOTES.id) {
        newExpandedEntryIds.delete(ALL_CHANNELS_SECTION_ID)
        newExpandedEntryIds.add(ARCHIVED_SECTION_ID)
      } else {
        newExpandedEntryIds.add(activeNote.channelId)

        // if the note is in a favorited channel, open the favorite section and close "all channels"
        if (favoriteSectionIds.includes(activeNote.channelId)) {
          newExpandedEntryIds.add(FAVORITE_CHANNELS_SECTION_ID)
          newExpandedEntryIds.delete(ALL_CHANNELS_SECTION_ID)
        } else {
          newExpandedEntryIds.add(ALL_CHANNELS_SECTION_ID)
          // keep favorites section state
          if (expandedEntryIds.has(FAVORITE_CHANNELS_SECTION_ID)) {
            newExpandedEntryIds.add(FAVORITE_CHANNELS_SECTION_ID)
          }
        }
      }

      // open all of the note's ancestores
      for (const activeNoteAncestorsId of activeNoteAncestorsIds) {
        newExpandedEntryIds.add(activeNoteAncestorsId)
      }

      // expand the note itself if it has children
      if (activeNote.hasChildren) {
        newExpandedEntryIds.add(activeNote.id)
      }
    }
  }

  const activeNotePathAsString = [
    ...activeNoteAncestorsIds,
    activeNote ? activeNote.id : '',
  ].join('-') // not taking the section into account on purpose

  // navigation to a note
  React.useEffect(
    () => {
      if (
        lastClickedNoteId !== activeNoteId &&
        activeNotePathAsString &&
        ![BOOKMARKED_NOTES.id, TEMPLATE_NOTES.id].includes(activeFilterId)
      ) {
        setExpandedEntryIds(newExpandedEntryIds)
      }
    },
    // ignore the issue in the array on purpose:
    // we convert/hash the activeNotePathIds to a string as comparing arrays does not work
    // as suggested in https://github.com/facebook/react/issues/14324#issuecomment-441489421
    // Spreading the array isn't a proper solution
    // (cf. https://stackoverflow.com/questions/57607252/useeffect-warning-when-adding-dependencies-using-spread-operator)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeNotePathAsString, activeFilterId]
  )

  // navigation to a template
  React.useEffect(
    () => {
      if (activeTemplateId) {
        setExpandedEntryIds(new Set([TEMPLATE_NOTES_SECTION_ID]))
      }
    },
    [activeTemplateId, setExpandedEntryIds]
  )

  // navigation to a channel (not a note)
  React.useEffect(
    () => {
      if (activeChannelId && !activeNoteId) {
        channelLikeIds.forEach(channelId => {
          expandedEntryIds.delete(channelId)
        })
        expandedEntryIds.add(activeChannelId)
        if (
          favoriteChannelIds.includes(activeChannelId) ||
          activeChannelId === personalChannelId
        ) {
          expandedEntryIds.add(FAVORITE_CHANNELS_SECTION_ID)
          expandedEntryIds.delete(ALL_CHANNELS_SECTION_ID)
        } else {
          expandedEntryIds.add(ALL_CHANNELS_SECTION_ID)
        }
        setExpandedEntryIds(new Set(expandedEntryIds))
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeChannelId, activeNoteId]
  )
}
